/**
 * 运营两种空态：A 该语言全局未配 / B 某 App 该语言无覆盖。
 *
 * 1:1 移植 design/Novel-admin/src/pages/ops-scope.jsx 的 GlobalEmpty / NoOverride。
 */

import { langName } from '~/lib/format';
import { Icons } from '~/lib/icons';
import {
  type OpsModule,
  opsCopyButtonDisabled,
  opsCopyButtonTitle,
  opsDirectCreateButtonDisabled,
  opsDirectCreateButtonTitle,
  opsModuleLabel,
  usesAppDedicatedCopy,
} from './ops-scope';

export function GlobalEmpty({ lang, what, hint }: { lang: string; what: string; hint?: string }) {
  return (
    <div className="card" style={{ padding: '34px 18px', textAlign: 'center' }}>
      <div style={{ fontSize: 13, fontWeight: 500 }}>
        当前语言 <span className="mono">{lang.toUpperCase()}</span> {langName(lang)} 暂无配置
      </div>
      <div
        style={{
          fontSize: 11.5,
          color: 'var(--fg-muted)',
          marginTop: 6,
          lineHeight: 1.6,
          maxWidth: 560,
          margin: '6px auto 0',
        }}
      >
        {what} 每种语言各有一份全局{hint ? `，${hint}` : ''}
        。新语言的全局默认空，等运营手动添加 —— 不从英语抄，也不回退成英语数据；空则接口不返回。
      </div>
    </div>
  );
}

export function NoOverride({
  module,
  appName,
  lang,
  globalN,
  onCopy,
  onDirectCreate,
  languageAllowed = true,
}: {
  module: OpsModule;
  appName: string;
  lang: string;
  globalN: number;
  onCopy: () => void;
  onDirectCreate: () => void;
  languageAllowed?: boolean;
}) {
  return (
    <div className="card" style={{ padding: '30px 18px', textAlign: 'center' }}>
      <span className="chip chip-warn" style={{ fontSize: 10 }}>
        无覆盖
      </span>
      <div style={{ fontSize: 13, fontWeight: 500, marginTop: 10 }}>
        {appName} 在 <span className="mono">{lang.toUpperCase()}</span> {langName(lang)}{' '}
        下没有独立配置
      </div>
      <div
        style={{
          fontSize: 11.5,
          color: 'var(--fg-muted)',
          marginTop: 6,
          lineHeight: 1.6,
          maxWidth: 560,
          margin: '6px auto 0',
        }}
      >
        C 端将使用该语言全局（{opsModuleLabel(module)} · {lang.toUpperCase()} 全局共 {globalN}{' '}
        条）。本表不列全局行；复制后两层解耦，全局后续改动不再影响这份覆盖。
      </div>
      {usesAppDedicatedCopy(module) && (
        <div className="hstack" style={{ gap: 8, justifyContent: 'center', marginTop: 14 }}>
          <button
            type="button"
            className="btn btn-accent"
            disabled={opsCopyButtonDisabled(globalN, languageAllowed)}
            title={opsCopyButtonTitle(globalN, languageAllowed, lang)}
            onClick={onCopy}
          >
            <Icons.IconCopy size={12} />
            从全局复制
          </button>
          <button
            type="button"
            className="btn"
            disabled={opsDirectCreateButtonDisabled(languageAllowed)}
            title={opsDirectCreateButtonTitle(languageAllowed, lang)}
            onClick={onDirectCreate}
          >
            <Icons.IconPlus size={12} />
            直接创建空覆盖
          </button>
        </div>
      )}
      {globalN === 0 && (
        <div style={{ fontSize: 11, color: 'var(--warn)', marginTop: 8 }}>
          该语言全局为空（0 条），暂无可复制的源 —— 先切到「全部」配置 {lang.toUpperCase()} 全局。
        </div>
      )}
    </div>
  );
}
